import { useEffect, useState } from 'react';
import type { ComponentType } from 'react';
import {
  BookOpen,
  CalendarClock,
  Check,
  ChevronRight,
  Download,
  File,
  LogOut,
  Monitor,
  Moon,
  Settings,
  Sun,
} from 'lucide-react';
import { ClientOnly, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@/features/theme/components/theme-provider';
import { useLayoutMode } from '@/features/reader/hooks/use-layout-mode';
import { authClient } from '@/features/auth/lib/auth-client';
import { useSession } from '@/features/auth/hooks/use-session';
import type { Chapter } from '@quranjs/api';
import chaptersJson from '@/data/chapters.json';

const chapters = chaptersJson as unknown as Chapter[];
import { useScheduledCards } from '@/features/memorization/hooks/use-scheduled-cards';
import { ReviewSchedule } from '@/features/memorization/components/review-schedule';
import { Separator } from '@/shared/components/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/sheet';
import { useOfflineCache } from '@/features/offline/hooks/use-offline-cache';
import { Progress } from '@/shared/components/progress';
import { cn } from '@/shared/lib/utils';

type Theme = 'dark' | 'light' | 'system';
type LayoutMode = 'single' | 'spread';

const themeOptions: {
  value: Theme;
  icon: ComponentType<{ className?: string }>;
  label: string;
}[] = [
  { value: 'light', icon: Sun, label: 'Light' },
  { value: 'dark', icon: Moon, label: 'Dark' },
  { value: 'system', icon: Monitor, label: 'System' },
];

const layoutOptions: {
  value: LayoutMode;
  icon: ComponentType<{ className?: string }>;
  label: string;
}[] = [
  { value: 'single', icon: File, label: '1 Page' },
  { value: 'spread', icon: BookOpen, label: '2 Pages' },
];

function OfflineDownloadSection() {
  const {
    status,
    cached,
    total,
    percent,
    error,
    downloadAll,
    cancel,
    refreshCount,
  } = useOfflineCache();

  useEffect(() => {
    void refreshCount();
  }, [refreshCount]);

  const isDone = status === 'done';
  const isDownloading = status === 'downloading';
  const showProgress = cached > 0 && !isDone;

  if (isDone) {
    return (
      <div className="flex items-center gap-3 px-3 py-2.5">
        <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Offline reading</span>
        <span className="ml-auto flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
          Saved
        </span>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={isDownloading ? cancel : () => void downloadAll()}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors hover:bg-accent active:bg-accent"
      >
        <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span>
          {isDownloading
            ? 'Downloading…'
            : cached > 0
              ? 'Continue download'
              : 'Download for offline'}
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          {isDownloading ? `${percent}%` : `${cached}/${total}`}
        </span>
      </button>

      {showProgress && (
        <div className="px-3 pb-1">
          <Progress value={percent} className="h-1" />
        </div>
      )}

      {error && (
        <p className="px-3 pb-1 text-[11px] text-destructive">{error}</p>
      )}
    </div>
  );
}

interface SettingsTriggerProps {
  isActive: boolean;
  onToggle: () => void;
}

export function SettingsTrigger({ isActive, onToggle }: SettingsTriggerProps) {
  return (
    <button
      type="button"
      aria-expanded={isActive}
      aria-label="Settings"
      onClick={onToggle}
      className={cn(
        'flex h-12 w-12 items-center justify-center rounded-full transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-foreground hover:bg-accent',
      )}
    >
      <Settings className="h-5 w-5" />
    </button>
  );
}

interface SettingsContentProps {
  onClose: () => void;
}

export function SettingsContent({ onClose }: SettingsContentProps) {
  const { theme, setTheme } = useTheme();
  const { layoutMode, setLayoutMode } = useLayoutMode();
  const { session, isAuthenticated } = useSession();
  const { dueNow, upcoming } = useScheduledCards(isAuthenticated);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);

  const dueCount = dueNow.length;
  const hasSchedule = dueNow.length > 0 || upcoming.length > 0;
  const user = session?.user;

  function handleNavigate(pageNumber: number) {
    setScheduleOpen(false);
    void navigate({
      to: '/page/$id',
      params: { id: String(pageNumber) },
    });
  }

  return (
    <>
      <div className="space-y-5 p-5">
        {/* Review schedule */}
        {isAuthenticated && hasSchedule && (
          <button
            type="button"
            onClick={() => setScheduleOpen(true)}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors hover:bg-accent active:bg-accent"
          >
            <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span>Review schedule</span>
            {dueCount > 0 && (
              <span className="ml-auto text-xs font-semibold text-gold">
                {dueCount} due
              </span>
            )}
            <ChevronRight
              className={cn(
                'h-3.5 w-3.5 shrink-0 text-muted-foreground',
                dueCount === 0 && 'ml-auto',
              )}
            />
          </button>
        )}

        {/* Layout + Offline */}
        <div className="space-y-2">
          <div className="flex rounded-xl bg-muted p-1">
            {layoutOptions.map(({ value, icon: Icon, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setLayoutMode(value)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-medium transition-all',
                  layoutMode === value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>

          <ClientOnly fallback={null}>
            <OfflineDownloadSection />
          </ClientOnly>
        </div>

        {/* Theme */}
        <div className="flex rounded-xl bg-muted p-1">
          {themeOptions.map(({ value, icon: Icon, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setTheme(value)}
              className={cn(
                'flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-medium transition-all',
                theme === value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Profile + Sign out */}
        {isAuthenticated && user && (
          <div>
            <Separator className="mb-5" />
            <div
              className={cn(
                'grid transition-all duration-200',
                confirmingSignOut
                  ? 'grid-rows-[0fr_1fr]'
                  : 'grid-rows-[1fr_0fr]',
              )}
            >
              <div className="overflow-hidden">
                <div className="flex items-center gap-3">
                  {user.image ? (
                    <img
                      src={user.image}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                      {(user.name?.[0] ?? user.email?.[0] ?? '?').toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {user.name}
                    </p>
                    {user.email && (
                      <p className="truncate text-xs text-muted-foreground">
                        {user.email}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmingSignOut(true)}
                    className="ml-auto flex shrink-0 items-center gap-2 rounded-lg px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    Sign out
                  </button>
                </div>
              </div>
              <div className="overflow-hidden">
                <div className="flex items-center gap-3 pt-3">
                  <LogOut className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Sign out?
                  </span>
                  <div className="ml-auto flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmingSignOut(false)}
                      className="rounded-lg px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        await authClient.signOut();
                        await queryClient.invalidateQueries({
                          queryKey: ['session'],
                        });
                        setConfirmingSignOut(false);
                        onClose();
                      }}
                      className="rounded-lg bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/20"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Review schedule sheet */}
      <Sheet open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[70dvh] overflow-y-auto rounded-t-2xl px-5 pb-6 pt-3"
          hideClose
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Review schedule</SheetTitle>
            <SheetDescription>Your review schedule</SheetDescription>
          </SheetHeader>

          <div className="mb-5 flex justify-center">
            <div className="h-1 w-10 rounded-full bg-border" />
          </div>

          <ReviewSchedule
            dueNow={dueNow}
            upcoming={upcoming}
            chapters={chapters}
            onNavigate={handleNavigate}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
